import { createContext, forwardRef, Fragment, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import fallbackSettingsDialogDataProvider from '../__internal__/FallbackSettingsDialogDP';
import { getSettingsDialogDataProvider } from '../__internal__/SettingsDialogDataProvider';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { Dialog } from '../../Dialog';
import { MessageDialog, useMessageDialogText } from '../../MessageDialog';
import { SettingsLayout } from '../layout/SettingsLayout';
import { SettingsStoreProvider, useSettingsStore } from '../store/SettingsStore';
import bundle from '../NovaComponents-Settings-gui-icu.properties';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SettingsDialogDataProviderContext = /*#__PURE__*/createContext(fallbackSettingsDialogDataProvider);
export function useSettingsDialogDataProvider() {
  return useContext(SettingsDialogDataProviderContext);
}
const useTextLocalizer = createUseTextLocalizer(bundle);
const SettingsDialogContent = /*#__PURE__*/forwardRef((props, ref) => {
  const localize = useTextLocalizer();
  const {
    CONTINUE,
    CANCEL
  } = useMessageDialogText();
  const dataProvider = props.dataProvider ?? getSettingsDialogDataProvider();
  const store = useSettingsStore();
  const [shouldClose, setShouldClose] = useState(false);
  const closeHandle = props.onClose;
  const layoutRef = useRef(null);
  const hasDirtyFields = useCallback(() => {
    return store.hasDirtyFields();
  }, [store]);
  const focusOnLayout = () => {
    layoutRef.current?.focus();
  };
  useEffect(() => {
    if (shouldClose) {
      closeHandle();
    }
    return () => {
      setShouldClose(false);
    };
  }, [shouldClose, closeHandle]);
  useImperativeHandle(ref, () => ({
    hasDirtyFields,
    focusOnLayout
  }), [hasDirtyFields]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(MessageDialog, {
      testId: "settings-dismissAction-formInError",
      type: "warning",
      isOpen: props.isDirty,
      title: localize('settings.dismissAction.formInError.dialog.title'),
      text: localize('settings.formInError.warning.msg'),
      firstAction: {
        text: CONTINUE,
        onPress: () => {
          store.clearDirtyFields();
          props.setDirty(false);
          setShouldClose(true);
        }
      },
      dismissAction: {
        text: CANCEL,
        onPress: () => {
          props.setDirty(false);
        },
        isDefault: true
      }
    }), /*#__PURE__*/_jsx(SettingsDialogDataProviderContext.Provider, {
      value: dataProvider,
      children: /*#__PURE__*/_jsx(SettingsLayout, {
        testId: "settings-layout",
        initialFocusRef: layoutRef,
        customForms: props.customForms
      })
    })]
  });
});
export const SettingsDialog = props => {
  const localize = useTextLocalizer();
  const settingDialogRef = useRef({
    hasDirtyFields: () => false,
    focusOnLayout: () => undefined
  });
  const [isDirty, setDirty] = useState(false);
  function closeHandler() {
    if (settingDialogRef.current.hasDirtyFields()) {
      setDirty(true);
    } else {
      props.onClose();
    }
  }
  /*
    Dialog maxHeight and maxWidth needs some improvement and will remove this once dialog handles it properly. 
    So temporary adding the below maxHeight and maxWidth calculation.
  */
  return /*#__PURE__*/_jsx(Dialog, {
    testId: "settings",
    size: {
      width: '60rem',
      height: 768,
      maxWidth: 'calc(100% - 4rem)',
      maxHeight: 'calc(100% - 4rem)'
    },
    style: {
      '--dialog-scale-enter': '1'
    },
    isOpen: props.isOpen,
    title: localize('settings.dialog.title'),
    dismissAction: {
      text: localize('settings.dialog.dismissAction.button.label'),
      onPress: closeHandler
    },
    onOpen: () => settingDialogRef.current?.focusOnLayout(),
    focusLockProps: {
      autoFocus: false
    },
    children: /*#__PURE__*/_jsx(SettingsStoreProvider, {
      children: /*#__PURE__*/_jsx(SettingsDialogContent, {
        ref: settingDialogRef,
        customForms: props.customForms,
        dataProvider: props.dataProvider,
        isDirty: isDirty,
        setDirty: setDirty,
        onClose: props.onClose
      })
    })
  });
};
//# sourceMappingURL=SettingsDialog.js.map